import type { FilterResponse } from 'types';

import type { AutocompleteRenderValueGetItemProps } from '@mui/material';
import type { ComboBoxOption, ComboBoxApi } from 'components/inputs/types';
import type { TableState } from 'table/types';
import type { FilterParams } from 'types/table';
import { t } from '@lingui/macro';
import { commenProps } from 'table/components/filter/FilterRow';
import { trans } from 'utils/trans';
import { type ChipProps } from 'components/chip/Chip';
import { type TextProps } from 'components/text/Text';
import { Autocomplete } from 'components/inputs/Autocomplete';

export const transformFilter = (data: unknown, field?: string) => {
  const filterResponse = data as FilterResponse;
  if (!filterResponse?.data || !Array.isArray(filterResponse.data)) {
    return [];
  }

  const filterField = filterResponse.data.find(
    d => d.filterFieldName.toLowerCase() === field?.toLowerCase()
  );
  if (!filterField) {
    return [];
  }
  return filterField.options.map(d => ({ ...d, id: d.id, label: d.displayName }));
};

export const getComboBox = (
  id: string,
  label: string,
  api: ComboBoxApi = null,
  opts: ComboBoxOption[] | undefined = undefined,
  optionsChipRender?: (option: ComboBoxOption) => React.ReactNode,
  optionsChipProps?: ChipProps & { labelProps?: TextProps },
  isInvalidOption?: (option: ComboBoxOption) => boolean,
  vars?: string[]
) => {
  // `commenProps` carries `inputVariant`, which the Autocomplete wrapper omits in favour of
  // `variant` — strip it before spreading so the props object stays assignable.
  const { inputVariant, ...autocompleteCommonProps } = commenProps;
  const box = (
    value: ComboBoxOption[] | undefined,
    onChange: (val: ComboBoxOption[]) => void,
    dataTestId?: string
  ) => (
    <Autocomplete
      {...autocompleteCommonProps}
      renderValue={(
        _value: ComboBoxOption[],
        _getItemProps: AutocompleteRenderValueGetItemProps<true>
      ) => null}
      multiple
      api={api ? { ...api, ...(vars !== undefined ? { vars } : {}) } : undefined}
      options={opts}
      id={id}
      variant="outlined"
      value={value}
      onChange={(e, val) => onChange(val as ComboBoxOption[])}
      placeholder={`${t`Select`} ${trans(label)}`}
      disableClearable
      disableCloseOnSelect
      isInvalidOption={isInvalidOption}
      optionsChipRender={optionsChipRender}
      optionsChipProps={optionsChipProps}
      isOptionEqualToValue={(option, selectedValue) => {
        return Boolean(String(option?.id) === String(selectedValue?.id));
      }}
      data-testid={dataTestId}
      enableTooltip
    />
  );
  return box;
};

export const getQuery = (state: TableState, filters: FilterParams) => {
  const params = new URLSearchParams();
  if (state.search != null) params.append('search', state.search);
  if (state.sort != null) params.append('sort', state.sort);
  if (state.order != null) params.append('order', state.order);
  const filterState = state as Record<string, string>;
  if (filters && filterState) {
    filters.forEach(filter => {
      if (typeof filter === 'string') {
        if (filterState[filter] != null) {
          params.append(filter, filterState[filter]);
        }
      } else {
        const [paramKey, key, func] = filter;
        if (filterState[key]) {
          const value = func(filterState);
          if (value) {
            params.append(paramKey, value);
          }
        }
      }
    });
  }
  return params;
};
